import React, { useState, useEffect } from "react";
import confessAndBuyWithTokenABI from "./../utils/abi.json"; // Ensure this path is correct
import { BrowserProvider, Contract, parseEther, formatEther } from "ethers"; // v6 import

// import "./../App.css";
import "./../bitcoinfomo.css"; // Ensure you have this CSS file for styles
import {
  Wallet,
  ExternalLink,
  TrendingUp,
  MessageSquare,
  Coins,
  AlertTriangle,
  Copy,
  LogOut,
} from "lucide-react";
import CountdownSection from "./CountDownPage.jsx";

const BitcoinFOMODApp = () => {
  const [account, setAccount] = useState("");
  const [ethBalance, setEthBalance] = useState("0");
  const [fomoBalance, setFomoBalance] = useState("0");
  const [usdtBalance, setUsdtBalance] = useState("0");
  const [buyAmount, setBuyAmount] = useState("");
  const [regretMessage, setRegretMessage] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isBuying, setIsBuying] = useState(false);
  const [networkCorrect, setNetworkCorrect] = useState(false);

  const targetDate = new Date("2025-11-30T18:00:00Z").getTime();
  // console.log("Target date:", targetDate);
  const [timeLeft, setTimeLeft] = useState(targetDate - Date.now());
  const [isComplete, setIsComplete] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      // console.log("Checking time left...", targetDate, Date.now());
      const diff = targetDate - Date.now();
      // console.log("Time left:", diff);
      if (diff <= 0) {
        clearInterval(interval);
        setIsComplete(true);
      } else {
        setTimeLeft(diff);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  // Base Chain Configuration
  const FOMO_TOKEN_ADDRESS = "0x742d35Cc6634C0532925a3b8D6aF7738"; // Replace with actual token address
  const UNISWAP_BASE_URL =
    "https://app.uniswap.org/#/swap?inputCurrency=" +
    process.env.REACT_APP_USDT +
    "&outPutCurrency=" +
    process.env.REACT_APP_FOMO_TOKEN_ADDRESS +
    "&chain=sepolia";

  const ETH_CHAIN_ID = "0xAA36A7"; // Sepolia Testnet Chain ID (5 in hex)
  const ETH_RPC_URL = "https://ethereum-sepolia-rpc.publicnode.com"; // Replace with your Infura project ID

  // Check if wallet is connected and on correct network
  useEffect(() => {
    checkConnection();
    if (window.ethereum) {
      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);
    }
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );
        window.ethereum.removeListener("chainChanged", handleChainChanged);
      }
    };
  }, []);

  const checkConnection = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({
          method: "eth_accounts",
        });
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          console.log("Connected account:", accounts[0], account);
          await checkNetwork();
          await updateBalances(accounts[0]);
        }
      } catch (error) {
        console.error("Error checking connection:", error);
      }
    }
  };

  const checkNetwork = async () => {
    try {
      const chainId = await window.ethereum.request({ method: "eth_chainId" });

      setNetworkCorrect(chainId.toLowerCase() === ETH_CHAIN_ID.toLowerCase());
    } catch (error) {
      console.error("Error checking network:", error);
    }
  };

  const switchToBase = async () => {
    try {
      console.log("Switching to Base network...");

      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: ETH_CHAIN_ID }],
      });

      setNetworkCorrect(true);
      updateBalances(account);

      console.log("Switched to Base network successfully");
    } catch (switchError) {
      if (switchError.code === 4902) {
        console.error("Base network not found, adding it...");
        try {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: ETH_CHAIN_ID,
                chainName: "Base",
                nativeCurrency: {
                  name: "Ethereum",
                  symbol: "ETH",
                  decimals: 18,
                },
                rpcUrls: [ETH_RPC_URL],
                blockExplorerUrls: ["https://sepolia.etherscan.io"],
              },
            ],
          });
        } catch (addError) {
          console.error("Error adding Base network:", addError);
        }
      }
    }
  };
  useEffect(() => {
    if (account) updateBalances(account);
  }, [networkCorrect, account]);

  const handleAccountsChanged = (accounts) => {
    if (accounts.length > 0) {
      setAccount(accounts[0]);
      updateBalances(accounts[0]);
    } else {
      setAccount("");
      setEthBalance("0");
      setFomoBalance("0");
      setUsdtBalance("0");
    }
  };

  const handleChainChanged = (chainId) => {
    setNetworkCorrect(chainId === ETH_CHAIN_ID);
    if (account) updateBalances(account);
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert("Please install MetaMask to use this dApp");
      return;
    }

    setIsConnecting(true);
    try {
      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts",
      });
      setAccount(accounts[0]);
      await checkNetwork();
      await updateBalances(accounts[0]);
    } catch (error) {
      console.error("Error connecting wallet:", error);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    setAccount("");
    setEthBalance("0");
    setFomoBalance("0");
    setUsdtBalance("0");
  };

  const updateBalances = async (address) => {
    if (!window.ethereum || !address) return;

    try {
      // Get ETH balance
      const ethBal = await window.ethereum.request({
        method: "eth_getBalance",
        params: [address, "latest"],
      });
      // const ethInEther = (parseInt(ethBal, 16) / 1e18).toFixed(4);
      const ethInEther = formatEther(ethBal.toString());
      setEthBalance(ethInEther);
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      console.log("Using signer:", signer);
      const fomoToken = new Contract(
        process.env.REACT_APP_FOMO_TOKEN_ADDRESS,
        confessAndBuyWithTokenABI,
        signer
      );
      console.log("FOMO Token Contract:", fomoToken);
      const usdtToken = new Contract(
        process.env.REACT_APP_USDT,
        confessAndBuyWithTokenABI,
        signer
      );
      console.log("USDT Token Contract:", usdtToken);
      let fomoBalance = await fomoToken.balanceOf(address);
      console.log("FOMO Balance:", fomoBalance);
      // fomoBalance = (parseInt(fomoBalance, 18) / 1e18).toFixed(4);
      fomoBalance = formatEther(fomoBalance.toString());
      let usdtBalance = await usdtToken.balanceOf(address);
      // usdtBalance = (parseInt(usdtBalance, 18) / 1e18).toFixed(4);
      usdtBalance = formatEther(usdtBalance.toString());
      setFomoBalance(fomoBalance.toString());
      setUsdtBalance(usdtBalance.toString());
    } catch (error) {
      console.error("Error updating balances:", error);
    }
  };

  const handleBuyWithRegret = async () => {
    if (!account || !networkCorrect) {
      alert("Please connect wallet and switch to Base network");
      return;
    }

    if (!buyAmount || parseFloat(buyAmount) <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    if (!regretMessage.trim()) {
      alert("Please share your regret message");
      return;
    }

    setIsBuying(true);
    try {
      // This is a simplified example - you'd need to implement actual smart contract interaction
      // For now, we'll simulate the transaction
      console.log("Buying tokens with regret message:", {
        amount: buyAmount,
        message: regretMessage,
        account,
      });

      // Interact with the smart contract
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      console.log("Using signer:", signer);
      console.log("Contract address:", process.env.REACT_APP_FOMO_ENTRY);
      const contract = new Contract(
        process.env.REACT_APP_FOMO_ENTRY,
        confessAndBuyWithTokenABI,
        signer
      );
      console.log("Contract instance created:", contract);
      // approve contract amount if needed
      const tokenContract = new Contract(
        process.env.REACT_APP_USDT,
        confessAndBuyWithTokenABI,
        signer
      );

      // const regretContract = new Contract(
      //   process.env.REACT_APP_REGRETWALL_ADDRESS,
      //   confessAndBuyWithTokenABI,
      //   signer
      // );
      // console.log(
      //   "Setting executor for regret contract...",
      //   process.env.REACT_APP_FOMO_ENTRY
      // );
      // await regretContract.setExecutor(process.env.REACT_APP_FOMO_ENTRY);
      // console.log(
      //   "Executor of Fomo Contract:",
      //   await regretContract.isExecutor(process.env.REACT_APP_FOMO_ENTRY)
      // );
      console.log("Checking token allowance...");
      const allowance = await tokenContract.allowance(
        account,
        process.env.REACT_APP_FOMO_ENTRY
      );

      if (allowance < parseEther(buyAmount)) {
        const approveTx = await tokenContract.approve(
          process.env.REACT_APP_FOMO_ENTRY,
          parseEther(buyAmount)
        );
        await approveTx.wait();
        console.log("Token approved successfully");
      } else {
        console.log("Sufficient allowance already set");
      }

      console.log(
        "Calling contract method to confess and buy tokens...",
        regretMessage,
        parseEther(buyAmount)
      );
      // Call the contract method (adjust params as needed)
      // string calldata message,
      // address tokenIn,
      // uint256 amountIn,
      // uint256 minTokensOut
      // const regretOf = aw
      const tx = await contract.confessAndBuyWithToken(
        regretMessage,
        process.env.REACT_APP_USDT,
        parseEther(buyAmount),
        0
      );

      await tx.wait();

      alert(
        `Transaction submitted! Your regret "${regretMessage}" will be immortalized on the blockchain!`
      );
      setBuyAmount("");
      setRegretMessage("");
      await updateBalances(account);
    } catch (error) {
      console.error("Error buying tokens:", error);
      alert("Transaction failed. Please try again.");
    } finally {
      setIsBuying(false);
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(account);
    alert("Address copied to clipboard!");
  };

  const formatAddress = (addr) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <>
      {/* Google Analytics */}
      <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=G-RZPVNZL41J"
      ></script>
      <script
        dangerouslySetInnerHTML={{
          __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-RZPVNZL41J');
        `,
        }}
      />
      {/* Condition here */}
      {!isComplete ? (
        <CountdownSection />
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-orange-400 via-red-500 to-pink-600">
          {/* Header */}
          <header className="bg-black/20 backdrop-blur-md border-b border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-4">
                <div className="flex items-center space-x-3">
                  {/* Logo container */}
                  <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
                    <img
                      src="/logo-v2.gif" // if logo is in public/
                      // src
                      alt="Logo"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* App name */}
                  <h1 className="text-2xl font-bold text-white">BitcoinFOMO</h1>
                </div>
                <div className="flex items-center space-x-4">
                  {!networkCorrect && account && (
                    // console.log("Network is incorrect, showing switch button")

                    <button
                      onClick={switchToBase}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                    >
                      Switch to Sepolia
                    </button>
                  )}

                  {!account ? (
                    <button
                      onClick={connectWallet}
                      disabled={isConnecting}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 disabled:opacity-50"
                    >
                      <Wallet className="w-5 h-5" />
                      <span>
                        {isConnecting ? "Connecting..." : "Connect Wallet"}
                      </span>
                    </button>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <div className="bg-black/30 rounded-lg px-4 py-2 text-white">
                        <div className="text-xs opacity-70">
                          ETH: {ethBalance}
                        </div>
                        <div className="text-xs opacity-70">
                          FOMO: {fomoBalance}
                        </div>

                        <div className="text-xs opacity-70">
                          USDT: {usdtBalance}
                        </div>
                      </div>
                      <button
                        onClick={copyAddress}
                        className="flex items-center space-x-2 bg-black/30 text-white px-3 py-2 rounded-lg hover:bg-black/40 transition-colors"
                      >
                        <span className="text-sm">
                          {formatAddress(account)}
                        </span>
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={disconnectWallet}
                        className="p-2 bg-red-600/80 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <LogOut className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
                What's BitcoinFOMO?
              </h2>
              <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-4xl mx-auto leading-relaxed">
                The first meme coin that lets you immortalize your crypto
                regrets – on-chain.
              </p>

              <div className="bg-black/20 backdrop-blur-md rounded-3xl p-8 mb-12 border border-white/10">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Your message of missed opportunity will be:
                </h3>
                <div className="grid md:grid-cols-3 gap-6 text-white">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                      <Coins className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Permanently engraved</div>
                      <div className="text-sm opacity-70">
                        on the blockchain
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Publicly viewable</div>
                      <div className="text-sm opacity-70">
                        on the Wall of Regret™
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-red-500 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">
                        Funny, tragic, or legendary
                      </div>
                      <div className="text-sm opacity-70">
                        win big with great stories
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-black/30 backdrop-blur-md rounded-3xl p-8 mb-12 border border-white/10">
              <h3 className="text-3xl font-bold text-white text-center mb-8">
                How It Works
              </h3>

              <div className="max-w-2xl mx-auto space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    1
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-2">
                      Enter how many $BitcoinFOMO coins you want to buy
                    </h4>
                    <input
                      type="number"
                      value={buyAmount}
                      onChange={(e) => setBuyAmount(e.target.value)}
                      placeholder="Enter amount (ETH)"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-white/50 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    2
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-2">
                      Write your Regret Message
                    </h4>
                    <p className="text-white/70 text-sm mb-3">
                      Who are you blaming? What happened? The funnier/sadder the
                      better.
                    </p>
                    <textarea
                      value={regretMessage}
                      onChange={(e) => setRegretMessage(e.target.value)}
                      placeholder="I sold my Bitcoin at $100 because my wife said it was just a fad... she was wrong about everything!"
                      rows={4}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-white/50 focus:outline-none resize-none"
                    />
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    3
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-4">
                      Click Mint & Buy
                    </h4>
                    <div className="space-y-3">
                      <button
                        onClick={handleBuyWithRegret}
                        disabled={isBuying || !account || !networkCorrect}
                        className="w-full py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white font-bold text-lg rounded-lg hover:from-green-600 hover:to-blue-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
                      >
                        {isBuying ? "Processing..." : "Mint & Buy BitcoinFOMO"}
                      </button>

                      <div className="text-center">
                        <span className="text-white/70 text-sm">or</span>
                      </div>

                      <a
                        href={UNISWAP_BASE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center space-x-2 w-full py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                      >
                        <span>Buy on Uniswap</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="text-center text-white/80 text-sm">
              <p className="mb-4">
                Once you click on Mint & Buy above, you will be rerouted to
                complete your purchase while your message goes on-chain forever,
                and your tokens go in your wallet.
              </p>
              <p className="text-2xl font-bold text-yellow-300">
                Don't Miss Out Again
              </p>
            </div>
          </main>

          {/* Footer */}
          <footer className="bg-black/30 backdrop-blur-md border-t border-white/10 mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="flex flex-wrap justify-center items-center space-x-8 space-y-4 md:space-y-0">
                <a
                  href="#"
                  className="text-white hover:text-yellow-300 transition-colors font-medium"
                >
                  White Paper
                </a>
                <a
                  href=" https://x.com/BitcoinFomo01"
                  className="text-white hover:text-yellow-300 transition-colors font-medium"
                >
                  Join Telegram
                </a>
                <a
                  href="#"
                  className="text-white hover:text-yellow-300 transition-colors font-medium"
                >
                  Contact Us
                </a>
                <a
                  href="https://discord.com/users/1421504291566522430"
                  className="text-white hover:text-yellow-300 transition-colors font-medium"
                >
                  Join Discord
                </a>
              </div>

              {/* Add legal links below */}
              <div className="flex flex-wrap justify-center items-center space-x-8 space-y-4 md:space-y-0 mt-8">
                <a
                  href="/terms-of-use"
                  className="text-yellow-300 hover:text-white transition-colors font-medium underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>
                <a
                  href="/privacy-policy"
                  className="text-yellow-300 hover:text-white transition-colors font-medium underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                <a
                  href="/disclaimers"
                  className="text-yellow-300 hover:text-white transition-colors font-medium underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Disclaimers
                </a>
              </div>

              <div className="text-center mt-8 text-white/60 text-sm">
                <p>
                  &copy; 2025 BitcoinFOMO. All rights reserved. Your regrets,
                  immortalized forever.
                </p>
              </div>
            </div>
          </footer>
        </div>
      )}
    </>
  );
};

export default BitcoinFOMODApp;
